'use client';
// Retrait d'urgence (design isRetrait) — gèle l'applicabilité, notifie
// « ne pas appliquer », action irréversible journalisée avec signature.
import React, { useState } from 'react';
import { useGed } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { TitrePage, I, card, inputStyle, StatePill, EmptyState, Skeleton, enChargement, ChampRecherche} from '@/components/ui';
import { Norme } from '@/components/Norme';

import { Bouton } from '@/shared/Bouton';
export function Retrait() {
  const { docs, act, openDoc, session, loading, booted } = useGed();
  /* BLOQ-03 : sans cette garde, l'écran affirmait « Aucun document en vigueur ·
     0 sur 0 » pendant le chargement, puis faisait apparaître MAN-CATALOGUE. Le
     compteur « 0 sur 0 » était le plus trompeur des deux : une phrase se
     soupçonne, un chiffre se croit. */
  const attend = enChargement(booted, loading, docs.length);
  const [selId, setSelId] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const [busy, setBusy] = useState(false);
  const [repId, setRepId] = useState<string | null>(null); // doc en cours de reprise
  const [repMotif, setRepMotif] = useState('');
  const [q, setQ] = useState('');
  const isRQ = session.roles.includes('rq');

  const envigueur = docs.filter((d) => d.statut === 'envigueur');
  // C'est l'écran où l'on agit dans l'urgence, et c'était le seul de la GED à
  // n'offrir ni recherche ni compteur : sur un fonds réel, retrouver le bon
  // document en faisant défiler coûte les secondes qui comptent.
  const qq = q.trim().toLowerCase();
  const candidates = qq
    ? envigueur.filter((d) => `${d.code} ${d.titre} ${d.discipline}`.toLowerCase().includes(qq))
    : envigueur;
  const sel = envigueur.find((d) => d.documentId === selId) || null;
  const actives = docs.filter((d) => d.statut === 'retire');

  const confirm = async () => {
    if (!sel || !motif.trim()) return;
    setBusy(true);
    const ok = await act(sel.documentId, 'retrait', { motif: motif.trim() });
    setBusy(false);
    if (ok) { setSelId(null); setMotif(''); }
  };

  // Reprise : sort le document de l'état « retiré » (terminal) et rouvre le
  // circuit complet. Un document retiré n'est plus condamné à vie (QA v2 BUG-12).
  const reprendre = async (documentId: string) => {
    if (!repMotif.trim()) return;
    setBusy(true);
    const ok = await act(documentId, 'reprendre', { motif: repMotif.trim() });
    setBusy(false);
    if (ok) { setRepId(null); setRepMotif(''); }
  };

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <TitrePage eyebrow={<>Sécurité — action tracée<Norme art="7.8" /></>} titre="Retrait d’urgence"
        sousTitre={<>
          Le retrait sort immédiatement le document du service et notifie chaque destinataire « ne pas appliquer ». À distinguer du gel de l’applicabilité (fiche du document), qui suspend l’évolution d’un document qui, lui, reste applicable.
          Un document retiré ne revient jamais directement en vigueur : il repasse par le circuit complet.
        </>} />

      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)', flex: 1 }}>Choisir le document en vigueur à retirer</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>{attend ? '…' : `${candidates.length} sur ${envigueur.length}`}</span>
            </div>
            <ChampRecherche valeur={q} onChange={setQ} placeholder="Rechercher (code, titre, discipline)…" style={{ ...inputStyle, height: 36, marginTop: 9 }} />
          </div>
          {attend && <Skeleton rows={3} height={45} />}
          {!attend && candidates.map((d) => (
            <button key={d.documentId} onClick={() => setSelId(d.documentId)} className="ged-row"
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'start', background: selId === d.documentId ? 'var(--cyan-50)' : 'none', border: 'none', borderTop: '1px solid var(--border-subtle)', padding: '12px 18px', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>{d.code}</span>
              <span style={{ flex: 1, fontSize: 13, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.titre}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>v{d.versionCourante}</span>
            </button>
          ))}
          {!attend && candidates.length === 0 && (
            <EmptyState
              titre={envigueur.length === 0 ? 'Aucun document en vigueur.' : 'Aucun document ne correspond à cette recherche.'}
              indice={envigueur.length === 0 ? 'Le retrait d’urgence ne concerne que les documents applicables.' : undefined}
              action={envigueur.length > 0 ? <Bouton taille="sm" onClick={() => setQ('')}>Effacer la recherche</Bouton> : undefined} />
          )}
        </div>

        {sel ? (
          <div style={{ ...card, borderColor: 'var(--red-100)', padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--red-600)', fontWeight: 700, fontSize: 15 }}>
              <I n="alert" s={18} />Confirmation du retrait
            </div>
            <div style={{ marginTop: 12, fontSize: 13.5, color: 'var(--text-strong)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{sel.code}</span> — {sel.titre}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 6 }}>
              {(sel.diffusion || []).length} destinataire(s) seront notifiés « ne pas appliquer ».
            </div>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', margin: '14px 0 6px' }}>Motif du retrait (obligatoire)</label>
            <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} placeholder="Ex. erreur critique détectée au §4 — risque patient." style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
            <button onClick={confirm} disabled={!motif.trim() || busy} className="ged-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 14, background: 'var(--red-600)', color: 'var(--text-on-primary)', border: 'none', borderRadius: 'var(--radius-md)', padding: '11px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', opacity: motif.trim() ? 1 : 0.55, fontFamily: 'var(--font-sans)' }}>
              <I n="alert" s={15} />{busy ? 'Retrait…' : 'Geler l’applicabilité & notifier'}
            </button>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0' }}>Action irréversible dans le circuit — journalisée avec signature.</p>
          </div>
        ) : (
          <div style={card}>
            <EmptyState titre="Aucun document sélectionné." indice="Choisissez d’abord un document en vigueur dans la liste de gauche." />
          </div>
        )}
      </div>

      {actives.length > 0 && (
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px', background: 'var(--red-50)', borderBottom: '1px solid var(--border-default)', fontSize: 13.5, fontWeight: 700, color: 'var(--red-600)' }}>
            Retraits actifs
          </div>
          {actives.map((d) => (
            <div key={d.documentId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <div className="ged-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', fontFamily: 'var(--font-sans)' }}>
                <button onClick={() => openDoc(d.code, 'journal')} style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, textAlign: 'start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--red-600)' }}>{d.code}</span>
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--text-strong)' }}>{d.titre}</span>
                  <StatePill statut={d.statut} />
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>{d.retraitMotif}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>{fmtDate(d.dateVigueur)}</span>
                </button>
                {isRQ && repId !== d.documentId && (
                  <button onClick={() => { setRepId(d.documentId); setRepMotif(''); }} style={{ flexShrink: 0, fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, border: '1.5px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--text-body)', borderRadius: 'var(--radius-md)', padding: '6px 12px', cursor: 'pointer' }}>Reprendre le circuit</button>
                )}
              </div>
              {isRQ && repId === d.documentId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 18px 14px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>La reprise rouvre un circuit complet (rédaction → vérification → approbation). Motif :</span>
                  <input autoFocus value={repMotif} onChange={(e) => setRepMotif(e.target.value)} placeholder="Motif de reprise…" style={{ ...inputStyle, height: 34, flex: 1, minWidth: 180, fontSize: 12.5 }} />
                  <button onClick={() => setRepId(null)} style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, border: 'none', background: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>Annuler</button>
                  <button disabled={busy || !repMotif.trim()} onClick={() => void reprendre(d.documentId)} style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, border: 'none', background: 'var(--color-primary)', color: 'var(--text-on-primary)', borderRadius: 'var(--radius-md)', padding: '7px 14px', cursor: repMotif.trim() ? 'pointer' : 'not-allowed', opacity: repMotif.trim() ? 1 : 0.5 }}>Confirmer la reprise</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
